import * as React from "react";

import { useIsMobile } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

import { Portal as PortalPrimitive } from "./_internal/portal";
import { Presence } from "./_internal/presence";
import { Slot } from "./_internal/slot";

interface AlertDialogContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  titleId: string;
  descriptionId: string;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const AlertDialogContext = React.createContext<AlertDialogContextValue | null>(null);

function useAlertDialogContext(component: string) {
  const ctx = React.useContext(AlertDialogContext);
  if (!ctx) throw new Error(`${component} must be used inside <AlertDialog.Root>`);
  return ctx;
}

interface RootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

function Root({ open: controlled, defaultOpen = false, onOpenChange, children }: RootProps) {
  const [internal, setInternal] = React.useState(defaultOpen);
  const isControlled = controlled !== undefined;
  const open = isControlled ? controlled : internal;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternal(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const id = React.useId();
  const triggerRef = React.useRef<HTMLElement | null>(null);

  const ctx = React.useMemo<AlertDialogContextValue>(
    () => ({
      open,
      setOpen,
      titleId: `${id}-title`,
      descriptionId: `${id}-desc`,
      triggerRef,
    }),
    [open, setOpen, id],
  );

  return <AlertDialogContext.Provider value={ctx}>{children}</AlertDialogContext.Provider>;
}

interface TriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  render?: React.ReactElement;
}

const Trigger = React.forwardRef<HTMLButtonElement, TriggerProps>(function Trigger(
  { render, onClick, type, children, ...props },
  forwardedRef,
) {
  const ctx = useAlertDialogContext("AlertDialog.Trigger");

  const setRef = (node: HTMLElement | null) => {
    ctx.triggerRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node as HTMLButtonElement);
    else if (forwardedRef) forwardedRef.current = node as HTMLButtonElement;
  };

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    onClick?.(e);
    if (!e.defaultPrevented) ctx.setOpen(true);
  };

  if (render) {
    const renderWithChildren =
      children !== undefined ? React.cloneElement(render, undefined, children) : render;
    return (
      <Slot ref={setRef} onClick={handleClick as never} {...props}>
        {renderWithChildren}
      </Slot>
    );
  }

  return (
    <button
      ref={setRef as React.Ref<HTMLButtonElement>}
      type={type ?? "button"}
      onClick={handleClick}
      aria-haspopup="dialog"
      aria-expanded={ctx.open}
      {...props}
    >
      {children}
    </button>
  );
});

function Portal({
  children,
  container,
}: {
  children: React.ReactNode;
  container?: Element | null;
}) {
  const ctx = useAlertDialogContext("AlertDialog.Portal");
  return (
    <Presence open={ctx.open}>
      {({ dataProps }) => (
        <PortalPrimitive container={container}>
          <PortalContext.Provider value={dataProps}>{children}</PortalContext.Provider>
        </PortalPrimitive>
      )}
    </Presence>
  );
}

// Pass data-state/data-open down to Backdrop + Popup without requiring props changes at call-sites.
const PortalContext = React.createContext<{
  "data-state": "open" | "closed";
  "data-open"?: "";
  "data-closed"?: "";
} | null>(null);

function Backdrop({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const dataProps = React.useContext(PortalContext) ?? ({ "data-state": "open" } as const);
  return (
    <div
      className={cn(
        "fixed inset-0 z-50 bg-black/60",
        "data-open:animate-in data-open:fade-in-0 data-open:duration-150",
        "data-closed:animate-out data-closed:fade-out-0 data-closed:duration-100",
        className,
      )}
      {...dataProps}
      {...props}
    />
  );
}

interface PopupProps extends React.HTMLAttributes<HTMLDivElement> {
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  variant?: "default" | "workspace";
}

const Popup = React.forwardRef<HTMLDivElement, PopupProps>(function Popup(
  { className, children, initialFocusRef, variant = "default", ...props },
  forwardedRef,
) {
  const ctx = useAlertDialogContext("AlertDialog.Popup");
  const dataProps = React.useContext(PortalContext) ?? ({ "data-state": "open" } as const);
  const isMobile = useIsMobile();
  const localRef = React.useRef<HTMLDivElement>(null);

  // body scroll lock
  React.useEffect(() => {
    if (!ctx.open || typeof document === "undefined") return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [ctx.open]);

  // Escape -> close (treated as Cancel)
  React.useEffect(() => {
    if (!ctx.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        ctx.setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ctx]);

  // Initial focus + focus return
  React.useEffect(() => {
    if (!ctx.open) return;
    const triggerAtOpen = ctx.triggerRef.current;
    const focusTarget =
      initialFocusRef?.current ??
      localRef.current?.querySelector<HTMLElement>(
        '[data-autofocus], button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ??
      null;
    focusTarget?.focus();
    return () => {
      triggerAtOpen?.focus?.();
    };
  }, [ctx.open, ctx.triggerRef, initialFocusRef]);

  const sheetBase =
    "fixed inset-x-0 bottom-0 z-50 bg-card rounded-t-2xl shadow-2xl max-h-[92dvh] overflow-y-auto overscroll-contain pt-2 px-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] data-open:animate-in data-open:slide-in-from-bottom data-open:duration-200 data-closed:animate-out data-closed:slide-out-to-bottom data-closed:duration-150";
  const modalBase =
    "fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rounded-2xl w-full max-w-md mx-4 p-6 shadow-2xl max-h-[85vh] overflow-y-auto";
  const workspaceBase = isMobile
    ? "fixed inset-0 z-50 flex h-dvh flex-col overflow-hidden bg-card px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-[max(env(safe-area-inset-top),1rem)]"
    : "fixed z-50 left-1/2 top-1/2 flex h-[min(820px,calc(100dvh-3rem))] w-[min(1120px,calc(100vw-3rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card p-6 shadow-2xl";

  const setRef = (node: HTMLDivElement | null) => {
    localRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  return (
    <div
      ref={setRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={ctx.titleId}
      aria-describedby={ctx.descriptionId}
      tabIndex={-1}
      className={cn(
        variant === "workspace" ? workspaceBase : isMobile ? sheetBase : modalBase,
        className,
      )}
      {...dataProps}
      {...props}
    >
      {isMobile && variant === "default" && (
        <div className="flex justify-center pb-3 pointer-events-none">
          <span className="h-1 w-10 rounded-full bg-muted-foreground/30" />
        </div>
      )}
      {children}
    </div>
  );
});

function Title({ className, id, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  const ctx = useAlertDialogContext("AlertDialog.Title");
  return <h2 id={id ?? ctx.titleId} className={className} {...props} />;
}

function Description({ className, id, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  const ctx = useAlertDialogContext("AlertDialog.Description");
  return <p id={id ?? ctx.descriptionId} className={className} {...props} />;
}

interface CloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  render?: React.ReactElement;
}

function Close({ render, onClick, type, children, ...props }: CloseProps) {
  const ctx = useAlertDialogContext("AlertDialog.Close");
  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    onClick?.(e);
    if (!e.defaultPrevented) ctx.setOpen(false);
  };

  if (render) {
    const renderWithChildren =
      children !== undefined ? React.cloneElement(render, undefined, children) : render;
    return (
      <Slot onClick={handleClick as never} {...props}>
        {renderWithChildren}
      </Slot>
    );
  }

  return (
    <button type={type ?? "button"} onClick={handleClick} {...props}>
      {children}
    </button>
  );
}

export const AlertDialog = {
  Root,
  Trigger,
  Portal,
  Backdrop,
  Popup,
  Title,
  Description,
  Close,
};
